/**
 * Site Plan Carousel Plugin for Search Results Map
 *
 * Renders a bottom-sheet carousel when the user taps a site plan area on mobile.
 * The plugin subscribes to the 'sitePlanTapped' custom DOM event dispatched by
 * the site plan click handlers so it stays completely decoupled from
 * sitePlan.plugin and interactiveSitePlan.plugin.
 *
 * Tap behaviour mirrors the marker mobile experience:
 *   - First tap  → open card in the carousel
 *   - Second tap → navigate (handled by the click handler, not this plugin)
 *
 * Single responsibility: display and dismiss the mobile card carousel in
 * response to site plan tap interactions.
 *
 * This plugin does NOT:
 * - Run on desktop (bails early if isDesktop() is true)
 * - Handle the hover tooltip (see sitePlanMiniCard.plugin)
 * - Handle marker cards (see carousel.plugin)
 * - Manage site plan overlay lifecycles (see sitePlan.plugin, interactiveSitePlan.plugin)
 * - Handle navigation or analytics (see handlers/)
 *
 * Reason to change: mobile card variants, touch-dismiss behaviour, or the
 * rendering path for site plan tap cards.
 *
 * @module searchResultsMap/plugins/sitePlanCarousel.plugin
 */

import { distinctUntilChanged, filter, map } from 'rxjs';
import { selectFetchResultsStatus, selectMapResults, selectSitePlanCards } from '../redux/selectors';
import { sitePlanTapped$ } from '../redux/observables';
import mapInteractionSlice from '../redux/interactionSlice';
import { mapMarkerToCardInformation } from '../services/mapper';
import MobileMapCarousel from '../components/mobileMapCarousel';
import { createCardElements } from '../components/carouselDom';
import MarkerType from '../enums/markerType';

const { actions: interactionActions } = mapInteractionSlice;

const OWNER_ID = 'site-plan-carousel';

/**
 * Creates the site plan carousel plugin.
 *
 * @returns {{ name: string, init: Function, start: Function, stop: Function }}
 */
export default function createSitePlanCarouselPlugin() {
  const pluginState = {
    started: false,
    mobileCarousel: null,
    isDesktopBreakpoint: null,
    dispatch: null,
    store$: null,
    getState: null,
    getBreakpoint: null,
    subscriptions: [],
  };

  function renderMobileCard(cardData) {
    if (!pluginState.mobileCarousel?.isAvailable) {
      return;
    }

    const cardElements = createCardElements([cardData]);
    if (!cardElements.length) {
      return;
    }

    pluginState.mobileCarousel.render(cardElements, [cardData], OWNER_ID);
  }

  function handleMobileCards(cards) {
    if (!cards?.length) {
      pluginState.mobileCarousel?.clear(OWNER_ID);
      return;
    }

    renderMobileCard(cards[0]);
  }

  function handleSitePlanTapped({ communityId }) {
    if (pluginState.isDesktopBreakpoint?.() ?? false) {
      return;
    }

    if (!communityId) {
      return;
    }

    const results = selectMapResults(pluginState.getState()) ?? [];
    const resultMatch = results.find(({ communityId: resultCommunityId, type }) =>
      resultCommunityId === communityId &&
      [
        MarkerType.HousingDevelopment.toString(),
        MarkerType.BuildablePlan.toString(),
      ].includes(type)
    );

    if (!resultMatch) {
      return;
    }

    const cardData = { ...mapMarkerToCardInformation(resultMatch), type: resultMatch.type };
    pluginState.dispatch(interactionActions.setSitePlanCards([cardData]));
  }

  function setupSubscriptions() {
    // Open the carousel when a site plan is tapped on mobile.
    pluginState.subscriptions.push(
      sitePlanTapped$.subscribe(handleSitePlanTapped),
    );

    // Render carousel when Redux state is updated with site-plan card data.
    pluginState.subscriptions.push(
      pluginState.store$.pipe(
        filter(() => !(pluginState.isDesktopBreakpoint?.() ?? false)),
        map(selectSitePlanCards),
        distinctUntilChanged(),
      ).subscribe((cards) => {
        handleMobileCards(cards);
      }),
    );

    // Clear cards when the layout breakpoint changes (e.g. desktop ↔ mobile resize).
    pluginState.subscriptions.push(
      pluginState.store$.pipe(
        map(() => pluginState.getBreakpoint()),
        distinctUntilChanged(),
      ).subscribe(() => {
        pluginState.dispatch(interactionActions.clearSitePlanCards());
      }),
    );

    // Dismiss the carousel when new search results are fetched.
    pluginState.subscriptions.push(
      pluginState.store$.pipe(
        map(selectFetchResultsStatus),
        distinctUntilChanged(),
      ).subscribe(() => {
        if (pluginState.mobileCarousel?.isVisible()) {
          pluginState.dispatch(interactionActions.resetSelectedMarkerAndCarouselCards());
        }
      }),
    );

    // Dismiss the carousel when the user touches outside the carousel area.
    const handleTouchStart = (event) => {
      if (!pluginState.mobileCarousel?.isVisible()) {
        return;
      }

      const isInsideCarousel = !!event.target.closest('[data-map-carousel]');
      if (!isInsideCarousel) {
        pluginState.dispatch(interactionActions.clearSitePlanCards());
      }
    };

    pluginState.container.addEventListener('touchstart', handleTouchStart);
    pluginState.subscriptions.push({
      unsubscribe: () => pluginState.container.removeEventListener('touchstart', handleTouchStart),
    });
  }

  function clearSubscriptions() {
    pluginState.subscriptions.forEach((sub) => sub?.unsubscribe?.());
    pluginState.subscriptions = [];
  }

  return {
    name: 'site-plan-carousel',

    async init(context) {
      context.state.sitePlanCarousel = { started: false };
    },

    async start(context) {
      if (pluginState.started) {
        return;
      }

      pluginState.isDesktopBreakpoint = context.services.isDesktopBreakpoint;
      pluginState.dispatch = context.services.dispatch;
      pluginState.store$ = context.services.store$;
      pluginState.getState = context.services.getState;
      pluginState.getBreakpoint = context.services.getBreakpoint;
      pluginState.container = context.root;

      pluginState.mobileCarousel = new MobileMapCarousel((detail) => {
        document.dispatchEvent(new CustomEvent('carouselScroll', { detail }));
      });

      setupSubscriptions();

      pluginState.started = true;
      context.state.sitePlanCarousel = { started: true };
    },

    async stop(context) {
      pluginState.dispatch(interactionActions.clearSitePlanCards());

      clearSubscriptions();

      pluginState.mobileCarousel?.destroy();
      pluginState.mobileCarousel = null;

      pluginState.isDesktopBreakpoint = null;
      pluginState.dispatch = null;
      pluginState.store$ = null;
      pluginState.getState = null;
      pluginState.getBreakpoint = null;
      pluginState.started = false;
      pluginState.container = null;

      delete context.state.sitePlanCarousel;
    },
  };
}
